import { createContext, useMemo, useState } from "react";

export const CartContext = createContext();

export default function CartProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [cart, setCart] = useState([]);

  // derived values — computed on render instead of stored in state
  const itemsAmount = useMemo(
    () => cart.reduce((sum, item) => sum + item.amount, 0),
    [cart]
  );
  const total = useMemo(
    () => cart.reduce((sum, item) => sum + item.regularPrice * item.amount, 0),
    [cart]
  );

  // add to cart (increments amount if the item already exists)
  const addToCart = (item) => {
    setCart((prevCart) => {
      const existing = prevCart.find((cartItem) => cartItem._id === item._id);
      if (existing) {
        return prevCart.map((cartItem) =>
          cartItem._id === item._id
            ? { ...cartItem, amount: cartItem.amount + 1 }
            : cartItem
        );
      }
      return [...prevCart, { ...item, amount: 1 }];
    });
    setIsOpen(true);
  };

  const removeFromCart = (id) => {
    setCart((prevCart) => prevCart.filter((item) => item._id !== id));
  };

  // free-text quantity (used when amount >= 10); falls back to 1 on invalid input
  const handleInput = (e, id) => {
    const value = parseInt(e.target.value);
    setCart((prevCart) =>
      prevCart.map((item) =>
        item._id === id
          ? { ...item, amount: isNaN(value) ? 1 : value }
          : item
      )
    );
    setIsOpen(true);
  };

  // dropdown quantity (used when amount < 10)
  const handleSelect = (e, id) => {
    const value = parseInt(e.target.value);
    setCart((prevCart) =>
      prevCart.map((item) =>
        item._id === id ? { ...item, amount: value } : item
      )
    );
  };

  const clearCart = () => setCart([]);

  // memoized so consumers don't re-render unless cart-derived values change
  const value = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      cart,
      addToCart,
      removeFromCart,
      itemsAmount,
      handleInput,
      handleSelect,
      total,
      clearCart,
    }),
    [isOpen, cart, itemsAmount, total]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
